<script>
	import { csvParse, autoType } from "d3-dsv";
	import LineChart from "./LineChart.svelte";
	import DualLineChart from "./DualLineChart.svelte";
	import ColumnChart from "./ColumnChart.svelte";
	import StackedColumnChart from "./StackedColumnChart.svelte";
	import StaticNumber from "./StaticNumber.svelte";
	import Glossary from "./Glossary.svelte";
	import outlooks from "./outlook.js";
	import { onMount } from "svelte";
	import url from "./url";
	import order from "./order";

	export let glossary;

	$: glossarySelected = $url.hash.replace("#", "");

	let selected = outlooks[0];

	function normalizeOutlookValue(value) {
		return String(value || "")
			.replace(/&nbsp;/gi, " ")
			.replace(/\u00a0/g, " ")
			.trim()
			.toLowerCase();
	}

	$: filteredRows = dataText.filter(
		(d) =>
			normalizeOutlookValue(selected.id) === normalizeOutlookValue(d.outlook) ||
			normalizeOutlookValue(selected.id) === normalizeOutlookValue(d.outlook2) ||
			selected.id === "allMeasures",
	);

	let lastGlossaryClick = "";

	export let dataCharts, dataText;

	//this makes sure that the tiles are in the right order
	dataText.sort(
		(a, b) => order.indexOf(a.measure) - order.indexOf(b.measure),
	);

	// Inject "i" buttons
	function spliceSlice(str, index, count, add) {
		if (index < 0) {
			index = str.length + index;
			if (index < 0) {
				index = 0;
			}
		}
		return str.slice(0, index) + (add || "") + str.slice(index + count);
	}

	function updateLastGlossaryClick(i) {
		lastGlossaryClick = i;
		console.log(lastGlossaryClick);
	}

	onMount(() => (window.updateLastGlossaryClick = updateLastGlossaryClick));

	let count = 0;

	// Process dataText
	dataText.forEach((e, i) =>
		Object.keys(e).forEach((el) => {
			if (typeof dataText[i][el] == "string") {
				count++;

				dataText[i][el] = dataText[i][el]
					.replace(/(?<=\[).+?(?=\])/g, (str) => {
						let href = str.match(/(?<=\().+?(?=\))/);
						let readable = str
							.replace(href, "")
							.replace("(", "")
							.replace(")", "");
						let html = `<a class="i" id="${"gloslink" + count}" onclick="updateLastGlossaryClick('${"gloslink" + count}')" href="#${href}">${readable}</a>`;
						return html;
					})
					.replace(/\[/g, "")
					.replace(/\]/g, "");
			}
		}),
	);

	function formatDate(dateStr) {
		if (!dateStr) return dateStr;
		const str = String(dateStr);
		const monthMap = {
			jan: "Jan",
			feb: "Feb",
			mar: "Mar",
			apr: "Apr",
			may: "May",
			jun: "June",
			jul: "July",
			aug: "Aug",
			sep: "Sept",
			oct: "Oct",
			nov: "Nov",
			dec: "Dec",
		};
		const longMatch = str.match(/^(\d{1,2})-([A-Za-z]+)-(\d{2})$/);
		if (longMatch) {
			const month = monthMap[longMatch[2].toLowerCase()];
			const year = 2000 + parseInt(longMatch[3]);
			if (!month) return str;
			return `${parseInt(longMatch[1])} ${month} ${year}`;
		}
		const shortMatch = str.match(/^([A-Za-z]+)-(\d{2})$/);
		if (shortMatch) {
			const month = monthMap[shortMatch[1].toLowerCase()];
			const year =
				parseInt(shortMatch[2]) >= 30
					? 1900 + parseInt(shortMatch[2])
					: 2000 + parseInt(shortMatch[2]);
			if (!month) return str;
			return `${month} ${year}`;
		}
		return str;
	}

	const fallbackSeriesColours = [
		"rgb(32, 96, 149)",
		"rgb(17, 140, 123)",
		"rgb(246, 96, 104)",
		"rgb(46, 161, 164)",
		"rgb(103, 73, 166)",
		"rgb(135, 26, 91)",
	];

	function escapeRegExp(str) {
		return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	}

	function getSeriesColumns(row) {
		if (!row?.measure || !dataCharts?.columns) return [];
		const key = row.measure;
		return dataCharts.columns.filter(
			(column) =>
				column.startsWith(`${key}-`) || column.startsWith(`${key}_`),
		);
	}

	function getSeriesLabelFromSuffix(row, index) {
		const columns = getSeriesColumns(row);
		const column = columns[index];
		if (!column) return null;

		const suffix = column
			.replace(new RegExp(`^${escapeRegExp(row.measure)}[-_]?`), "")
			.replace(/[_-]+/g, " ")
			.trim();

		return suffix ? `${suffix}:` : null;
	}

	function getSeriesTextLabel(row, index) {
		const suffixLabel = getSeriesLabelFromSuffix(row, index);
		if (suffixLabel) return suffixLabel;

		const configuredLabel =
			index === 0 ? row.directLabelUnit : row.directLabelUnit2;
		if (configuredLabel && String(configuredLabel).trim() !== "") {
			return `${String(configuredLabel).trim()}:`;
		}

		return `Series ${index + 1}:`;
	}

	function stripTrailingColon(label) {
		return String(label || "").replace(/:\s*$/, "").trim();
	}

	function hasSharedDualOutlook(row) {
		if (!hasSecondaryOutlook(row)) return false;
		return (
			normalizeOutlookValue(row.outlook) ===
			normalizeOutlookValue(row.outlook2)
		);
	}

	function hasSecondaryOutlook(row) {
		const outlook2 = normalizeOutlookValue(row?.outlook2);
		return outlook2 !== "" && outlook2 !== "change not assessed";
	}

	function hasSource(row) {
		const sourceText = String(row?.sourcetext || "").trim();
		const sourceUrl = String(row?.sourceURL || "").trim();
		return sourceText !== "" && sourceUrl !== "";
	}

	function getSeriesTextColour(row, index) {
		const category = getSeriesColumns(row)[index];
		if (!category) return fallbackSeriesColours[index % fallbackSeriesColours.length];

		if (category.includes("Female")) return "rgb(103, 73, 166)";
		if (category.includes("Male")) return "rgb(46, 161, 164)";

		if (row.type === "stackedColumn") {
			if (category.includes("sea")) return "rgb(17, 140, 123)";
			if (category.includes("land")) return "rgb(32, 96, 149)";
		} else {
			if (category.includes("sea")) return "rgb(32, 96, 149)";
			if (category.includes("biodiversity-1")) return "rgb(246, 96, 104)";
			if (category.includes("biodiversity-2")) return "rgb(32, 96, 149)";
		}

		if (category.includes("rural")) return "rgb(135, 26, 91)";
		if (category.includes("urban")) return "rgb(32, 96, 149)";

		return fallbackSeriesColours[index % fallbackSeriesColours.length];
	}
</script>

<!-- <div id="preText">
	<t>
		Because this is using the <a href="https://www.ons.gov.uk/peoplepopulationandcommunity/wellbeing/articles/ukmeasuresofnationalwellbeing/dashboard" target="_blank">measures of national wellbeing dashboard</a> as a basis we can have some introductory blurb at the top that includes links to the glossary at the bottom like this: <a
			class="i"
			href="#measure"
			on:click={() => updateLastGlossaryClick("preText")}>measure</a
		>
		a
		<a
			class="i"
			href="#improvelabel"
			on:click={() => updateLastGlossaryClick("preText")}
			>positive change</a
		>,
		<a
			class="i"
			href="#declinelabel"
			on:click={() => updateLastGlossaryClick("preText")}
			>negative change</a
		>,
		<a
			class="i"
			href="#nochange"
			on:click={() => updateLastGlossaryClick("preText")}>no change</a
		>, or
		<a
			class="i"
			href="#notassessed"
			on:click={() => updateLastGlossaryClick("preText")}
			>change not assessed</a
		>
		label based on assessment of change. See our
		<a
			class="i"
			href="#container"
			on:click={() => updateLastGlossaryClick("preText")}
			>definitions tool</a
		> for definitions of these labels and other terms that may be unfamiliar. Glossary links can also be put in the measures text. Or we could remove this and the definitions entirely
	</t>
</div> -->

<div id="filteringOptions">
	<div id="selectionChange">
		<label for="outlookSelect" class="filterLabel">Filter by:</label>
		<select
			id="outlookSelect"
			bind:value={selected}
			tabindex="0"
		>
			{#each outlooks as value}
				<option {value}>{@html value.title}</option>
			{/each}
		</select>
	</div>
</div>


{#each filteredRows as row}
		<hr class="dividerThin" />
		<div aria-live="assertive" id="tileshere" tabindex="-1">
			<div aria-hidden="true" id="top">
				<div aria-hidden="true" id="secondLayer" class="layer layer0">
					<div id="tile" class="tile tile0">
						<div class="containerTop containerTop0">
							<div class="BgColour">
								<div class="measure">
									{@html row.measuretitle}
								</div>
							</div>
							<div class="change">
								{#if hasSecondaryOutlook(row)}
									{#if hasSharedDualOutlook(row)}
										<p class="percent combinedPercent">
											<span class="combinedSeriesLeft" style={`color: ${getSeriesTextColour(row, 0)};`}>
												{stripTrailingColon(getSeriesTextLabel(row, 0))}
											</span>
											<span class="percentAnd"> and </span>
											<span class="combinedSeriesRight" style={`color: ${getSeriesTextColour(row, 1)};`}>
												{stripTrailingColon(getSeriesTextLabel(row, 1))}:
											</span>
										</p>
										<p class="outlook {row.outlook}">
											{@html row.outlook}
										</p>
									{:else}
										<p
											class={"percent " + row.measure + "1"}
											style={`color: ${getSeriesTextColour(row, 0)};`}>
											{getSeriesTextLabel(row, 0)}
										</p>
										<p class="outlook {row.outlook}">
											{@html row.outlook}
										</p>
										<p
											class={"percent " + row.measure + "2"}
											style={`color: ${getSeriesTextColour(row, 1)};`}>
											{getSeriesTextLabel(row, 1)}
										</p>
										<p class="outlook {row.outlook2}">
											{@html row.outlook2}
										</p>
									{/if}
								{:else}
									<p class="outlook {row.outlook}">
										{@html row.outlook}
									</p>
								{/if}
							</div>
						</div>
						{#if row.since}
							<div class="since">
								Since: {formatDate(row.since)}
							</div>
						{/if}
						<div class="title">{@html row.title}</div>
						<div class="commentary">{@html row.commstext}</div>
						{#if hasSource(row)}
							<div class="source">
								Source: <a href={row.sourceURL} target="_blank">
									{@html row.sourcetext}</a
								>
							</div>
						{/if}
					</div>
					<div aria-hidden="true" class="chart chart0">
						{#if row.type == "line"}
							<LineChart
								id="{row.measure}_chart"
								{row}
								{dataCharts}
								type="line"
							/>
						{/if}
						{#if row.type == "dualLine"}
							<DualLineChart
								id="{row.measure}_chart"
								{row}
								{dataCharts}
								type="dualLine"
							/>
						{/if}
						{#if row.type == "number"}
							<StaticNumber
								id="{row.measure}_chart"
								{row}
								{dataCharts}
								{dataText}
								type="number"
							/>
						{/if}
						{#if row.type == "bar"}
							<ColumnChart
								id="{row.measure}_chart"
								{row}
								{dataCharts}
								{dataText}
								type="bar"
							/>
						{/if}
						{#if row.type == "stackedColumn"}
							<StackedColumnChart
								id="{row.measure}_chart"
								{row}
								{dataCharts}
								type="stackedColumn"
							/>
						{/if}
					</div>
					{#if hasSource(row)}
						<div class="source2">
							Source: <a href={row.sourceURL} target="_blank">
								{@html row.sourcetext}</a
							>
						</div>
					{/if}
				</div>
			</div>
		</div>
{/each}

<div class="backToTop">
	<a href="#filteringOptions">&#x25B2; Back to the top</a>
</div>
<!-- {#each glossary as entry, i}
	<p style="visibility:hidden; height:0" id={entry.hash}>{glossary.label}</p>
{/each}

<Glossary {glossarySelected} {glossary} {lastGlossaryClick} /> -->

<style>
	.topline {
		margin-bottom: 5px;
	}

	a:visited {
		color: rgb(0, 100, 200);
	}
</style>
